import { LayoutGrid, Plus } from "lucide-react"

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@ibada/ui"

import { useDashboardEngineLabels } from "../model/engine-labels"

import type { DashboardWidget } from "../model/dashboard-types"

export interface AddWidgetsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  hiddenWidgets: DashboardWidget[]
  onRestore: (widgetId: string) => void
}

/**
 * Lists widgets currently hidden from the dashboard, each with an "Add" action that restores it to the
 * bottom of the shortest visible column. Shows a calm empty state when nothing is hidden.
 */
export function AddWidgetsDialog({ open, onOpenChange, hiddenWidgets, onRestore }: AddWidgetsDialogProps) {
  const labels = useDashboardEngineLabels()
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{labels.addWidgets}</DialogTitle>
          <DialogDescription>{labels.addDescription}</DialogDescription>
        </DialogHeader>

        {hiddenWidgets.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-8 text-center">
            <span className="inline-flex size-9 items-center justify-center rounded-full bg-muted text-muted-foreground">
              <LayoutGrid className="size-4" aria-hidden="true" />
            </span>
            <p className="text-sm font-medium">{labels.allVisible}</p>
            <p className="text-xs text-muted-foreground">{labels.allVisibleHint}</p>
          </div>
        ) : (
          <ul className="divide-y">
            {hiddenWidgets.map((widget) => (
              <li key={widget.id} className="flex items-center gap-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{widget.title}</p>
                  {widget.subtitle ? (
                    <p className="truncate text-xs text-muted-foreground">{widget.subtitle}</p>
                  ) : null}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="shrink-0"
                  onClick={() => onRestore(widget.id)}
                  aria-label={labels.addNamed(widget.title)}
                >
                  <Plus className="size-4" />
                  {labels.addShort}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  )
}
